import { useEffect, useState } from "react";
import { Download, X } from "lucide-react";

const DISMISS_KEY = "apollo_pwa_dismissed_at";
const DISMISS_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

function isStandalone() {
  if (typeof window === "undefined") return false;
  return window.matchMedia?.("(display-mode: standalone)").matches
    || window.navigator.standalone === true;
}

function isIOS() {
  const ua = navigator.userAgent || "";
  return /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
}

/**
 * "Install Apollo" prompt. Catches beforeinstallprompt on Chromium/Edge/Android and
 * falls back to a static iOS Safari card ("tap Share → Add to Home Screen") because
 * iOS has no programmatic install API. Dismissal is remembered for 7 days.
 */
export default function InstallPWA() {
  const [event, setEvent] = useState(null);
  const [visible, setVisible] = useState(false);
  const [iosPrompt, setIosPrompt] = useState(false);

  useEffect(() => {
    if (isStandalone()) return;
    const raw = localStorage.getItem(DISMISS_KEY);
    if (raw && Date.now() - Number(raw) < DISMISS_TTL_MS) return;

    const handler = (e) => {
      e.preventDefault();
      setEvent(e);
      setVisible(true);
    };
    window.addEventListener("beforeinstallprompt", handler);

    if (isIOS()) {
      // Show a light iOS-specific tip after a short delay so it doesn't fight
      // with the first paint.
      const t = setTimeout(() => { setIosPrompt(true); setVisible(true); }, 1200);
      return () => { clearTimeout(t); window.removeEventListener("beforeinstallprompt", handler); };
    }

    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  function dismiss() {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setVisible(false);
  }

  async function install() {
    if (!event) return;
    event.prompt();
    const { outcome } = await event.userChoice;
    if (outcome === "accepted") setVisible(false);
    setEvent(null);
  }

  if (!visible) return null;

  return (
    <div
      className="fixed left-1/2 -translate-x-1/2 z-40 w-[92%] max-w-md border border-border rounded-sm bg-surface shadow-2xl"
      style={{ bottom: "calc(env(safe-area-inset-bottom) + 12px)" }}
      data-testid="pwa-install-banner"
    >
      <div className="p-4 flex items-start gap-3">
        <div className="w-10 h-10 rounded-sm flex items-center justify-center shrink-0" style={{ background: "#E15C2C" }}>
          <Download className="h-5 w-5 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground">Add to home screen</div>
          <div className="font-display text-lg uppercase font-bold tracking-tight mt-0.5">Install Apollo</div>
          <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
            {iosPrompt
              ? "Tap the Share icon in Safari, then choose \"Add to Home Screen\" for one-tap access."
              : "Pin Apollo to your home screen — one tap opens the full brand portal."}
          </p>
          {!iosPrompt && event && (
            <button
              onClick={install}
              className="mt-3 bg-foreground text-background px-3 py-1.5 uppercase text-[10px] font-semibold tracking-[0.25em] flex items-center gap-2 rounded-sm"
              data-testid="pwa-install-btn"
            >
              <Download className="h-3 w-3" /> Install
            </button>
          )}
        </div>
        <button
          onClick={dismiss}
          className="shrink-0 p-1 hover:bg-surface-elev rounded-sm text-muted-foreground hover:text-foreground transition"
          aria-label="Dismiss"
          data-testid="pwa-install-dismiss"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
